import { CardBlock } from '@coast/schemas/index';
import { useFormik } from 'formik';
import { isEqual, isNil, merge } from 'lodash';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { useLayoutAnimation } from 'use-layout-animation';
import { Fields } from '@shared/components/blocks/types';
import {
  extractAllFields,
  extractBlockDefaultValues,
  extractCardDefinitionDefaultValues,
  extractFieldOverrides,
} from '@shared/components/blocks/utils';
import { validateBlocks } from '@shared/components/blocks/utils/validate';
import { CardFormSubformContainer } from '@shared/scenes/cards/form/components/CardFormSubformContainer';
import { useCardDefinition } from '@shared/scenes/cards/hooks';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { createCtx } from '@shared/util/context';
import { Values, getCardDraft, saveCardDraft } from '../utils/CardDraft';
import { CardChannelContext } from './channel';
import { CardRenderContext } from './render';

type Errors = { [key: string]: string };
type EventHookFunction = (values: Values) => void | Partial<Values> | Promise<Partial<Values>>;

export interface FormContext {
  values: Values;
  errors: Errors;
  isSubmitting: boolean;
  mode: 'EDIT' | 'CREATE';
  card: API.CardFragment;
  handleBlur(fieldName: string): () => void;
  setFieldValue(fieldName: string, value: any): void;
  setFieldError(fieldName: string, error: string | undefined): void;
  submitForm(): Promise<any>;
  registerPresubmitHook(name: string, hook: EventHookFunction): void;
  unregisterPresubmitHook(name: string): void;
}

const [useCardFormContext, CardFormContextProvider] = createCtx<FormContext>();

export { useCardFormContext, CardFormContextProvider };

interface Props {
  card: API.CardFragment;
  blocks: CardBlock[];
  cardDefinitionId?: string | null;
  fieldOverrides?: Fields;
  children: ReactNode;
  restoreSavedData?: boolean;
  onDirty?(dirty: boolean): void;
  onSubmit?(values: Values): void;
  onSubmitFormButtonCallback?(onSubmitForm: () => void): void;
}

function cleanReferences(value: Record<string, any>) {
  return JSON.parse(JSON.stringify(value));
}

function useSyncFormStateFromApolloCache(
  apolloCardFields: Dict<any>,
  formikValues: Dict<any>,
  setFieldValue: (name: string, value: any) => void,
) {
  const [lastSyncedCardFields, setLastSyncedCardFields] = useState(apolloCardFields);

  useEffect(() => {
    Object.keys(apolloCardFields).forEach((fieldName: string) => {
      const changedInApolloCache = !isEqual(
        apolloCardFields[fieldName],
        lastSyncedCardFields[fieldName],
      );
      const changedInFormByUser = !isEqual(
        lastSyncedCardFields[fieldName],
        formikValues[fieldName],
      );

      // TODO(fant): do we want to show the user an error if
      // the user has edited the field _and_ the field has
      // been updated in the Apollo cache (eg by someone else)?
      if (changedInApolloCache && !changedInFormByUser) {
        setFieldValue(fieldName, apolloCardFields[fieldName]);
      }
    });

    setLastSyncedCardFields(apolloCardFields);
  }, [apolloCardFields, formikValues, lastSyncedCardFields, setFieldValue]);
}

function CardFormProvider({
  blocks,
  card,
  cardDefinitionId,
  fieldOverrides,
  restoreSavedData,
  onDirty,
  onSubmit,
  onSubmitFormButtonCallback,
  children,
}: Props) {
  const channel = useContext(CardChannelContext);
  const channelId = channel?.id;
  const cardId = card?.id;
  const workflowTemplateId = channel?.workflowTemplateId;
  const { cardDefinition } = useCardDefinition(channelId, workflowTemplateId, cardDefinitionId);
  const renderContext = useContext(CardRenderContext);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const presubmitHooks = useRef<{ [key: string]: EventHookFunction }>({});
  const registerPresubmitHook = useCallback(
    (name: string, hook: EventHookFunction) => {
      presubmitHooks.current[name] = hook;
    },
    [presubmitHooks],
  );
  const unregisterPresubmitHook = useCallback(
    (name: string) => {
      delete presubmitHooks.current[name];
    },
    [presubmitHooks],
  );

  const submit = useCallback(
    async (values: Values) => {
      let formValues = { ...values };

      const hooks = Object.values(presubmitHooks.current);
      for (const hook of hooks) {
        const hookValues = await hook(formValues);
        formValues = {
          ...formValues,
          ...hookValues,
        };
      }

      return onSubmit?.(formValues);
    },
    [onSubmit, presubmitHooks],
  );

  const validate = useCallback(
    (values: Values) => {
      Keyboard?.dismiss();
      return validateBlocks(blocks, workflowTemplate, values);
    },
    [blocks, workflowTemplate],
  );

  const cardFields = useMemo(() => {
    const definition = cardDefinition?.definition ?? {};
    return merge(
      extractCardDefinitionDefaultValues(definition),
      extractBlockDefaultValues(blocks),
      extractAllFields(card, definition, false),
    );
  }, [blocks, card, cardDefinition?.definition]);

  const cardFieldsWithOverrides = useMemo(
    () => merge({}, cardFields, extractFieldOverrides(fieldOverrides)),
    [cardFields, fieldOverrides],
  );

  const {
    values,
    errors,
    isSubmitting,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    submitForm,
    setValues,
  } = useFormik<Values>({
    validate,
    onSubmit: submit,
    initialValues: cardFieldsWithOverrides,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    if (!restoreSavedData) {
      return undefined;
    }

    async function restoreCardDraft() {
      const data = await getCardDraft(channelId, cardId);
      if (isNil(data)) {
        return undefined;
      }
      setValues(data);
    }

    restoreCardDraft();
  }, [cardId, channelId, restoreSavedData, setValues]);

  useEffect(() => {
    if (renderContext !== 'form' || isNil(channelId)) {
      return;
    }
    saveCardDraft(values, channelId, cardId);
  }, [values, channelId, cardId, renderContext]);

  useEffect(() => {
    onSubmitFormButtonCallback?.(submitForm);
  }, [onSubmitFormButtonCallback, submitForm]);
  useLayoutAnimation([errors]);
  useSyncFormStateFromApolloCache(cardFieldsWithOverrides, values, setFieldValue);

  useEffect(() => {
    onDirty?.(!isEqual(cleanReferences(values), cleanReferences(cardFields)));
  }, [values, cardFields, onDirty]);

  const setFieldValueAndClearError = useCallback(
    (field: string, value: any) => {
      setFieldValue(field, value);
      setFieldError(field, undefined);
    },
    [setFieldError, setFieldValue],
  );

  const mode: FormContext['mode'] = card.id ? 'EDIT' : 'CREATE';

  const contextValue: FormContext = {
    values,
    isSubmitting,
    mode,
    errors: errors as Errors,
    card: card,
    setFieldError,
    setFieldValue: setFieldValueAndClearError,
    handleBlur: (fieldName: string) => () => setFieldTouched(fieldName, true),
    submitForm,
    registerPresubmitHook,
    unregisterPresubmitHook,
  };

  return (
    <CardFormContextProvider value={contextValue}>
      <CardFormSubformContainer>{children}</CardFormSubformContainer>
    </CardFormContextProvider>
  );
}

export function MBCardFormProvider(props: Props) {
  const channel = useContext(CardChannelContext);
  const { cardDefinition } = useCardDefinition(
    channel?.id,
    channel?.workflowTemplateId,
    props.cardDefinitionId,
  );
  if (!cardDefinition) {
    return null;
  }
  return <CardFormProvider {...props} key={props.card?.id || channel?.id} />;
}
